/**
 * Centralized tooltip descriptions for metric cards
 * 
 * This file contains all tooltip descriptions for headcount and utilization data
 * displayed in the Dashboard, My Insights, and My Teams pages.
 * 
 * Edit the descriptions here to update tooltips across all pages.
 */

export const metricTooltips = {
  // Headcount Metrics
  headcount: {
    activeHeadcount: "Total number of active employees currently in the organization",
    allocatedHeadcount: "Number of employees who have been allocated to projects (both billable and non-billable)",
    billableHeadcount: "Number of employees allocated to client billable projects",
    internalHeadcount: "Number of employees allocated to internal projects",
    benchStrength: "Number of employees who are not currently allocated to any project (available for allocation)",
    totalUtilization: "Percentage of active headcount that is allocated to projects (allocated headcount / active headcount)",
  },

  // Utilization Metrics
  utilization: {
    potentialBillableHours: "Total number of billable hours available based on active headcount and standard working hours",
    billableUtilization: "Percentage of potential billable hours that are allocated to client billable projects",
    nonBillableUtilization: "Percentage of potential billable hours allocated to non-billable activities (client non-billable and internal projects)",
    allocatedHours: "Total hours allocated across all projects, broken down by client billable, client non-billable, and internal projects",
    actualHours: "Total hours actually worked, broken down by client projects, internal projects, and general tasks",
    clientProjectVariance: "Percentage difference between actual client hours and allocated client billable hours. Positive values indicate over-allocation, negative values indicate under-allocation",
    internalProjectVariance: "Percentage difference between actual internal project hours and allocated internal hours. Positive values indicate over-allocation, negative values indicate under-allocation",
  },
};

/**
 * Helper function to get tooltip description by key
 * @param category - 'headcount' or 'utilization'
 * @param key - The specific metric key
 * @returns The tooltip description string
 */
export const getTooltip = (category: keyof typeof metricTooltips, key: string): string | undefined => {
  return metricTooltips[category]?.[key as keyof typeof metricTooltips[typeof category]];
};

/**
 * Shared MUI Tooltip props for metric summary cards (aligned with Utilization Insights / common MetricCard).
 */
export const metricSummaryTooltipProps = {
  arrow: true,
  placement: 'top' as const,
  enterDelay: 350,
  slotProps: {
    tooltip: {
      sx: {
        maxWidth: 320,
        textAlign: 'left' as const,
        whiteSpace: 'normal' as const,
        lineHeight: 1.45,
      },
    },
  },
};

